<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
            <crud-button
              v-if="$checkPermission('ROUTES')"
              x-large
              color="light-blue lighten-2"
              @clicked="create()"
              icon="fa-plus"
              :tooltip="$t('global.crud.button.add')"
            />
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col lg="2" sm="12" md="4" cols="12">
                  <v-text-field
                    v-model="filter.allFields"
                    :label="$t('global.datatable.search')"
                    append-icon="fas fa-search"
                    single-line
                  ></v-text-field>
                </v-col>
                <v-col lg="3" sm="12" md="4" cols="12">
                  <v-autocomplete
                    v-model="filter.agencyOriginCode"
                    :items="agenciesOriginFilterItems"
                    :loading="agenciesOriginFilter.isLoading"
                    :search-input.sync="agenciesOriginFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('routes.agencyOrigin')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesOriginFilter.isEdit = true"
                    @change="agenciesOriginFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col lg="2" sm="12" md="4" cols="12">
                  <v-autocomplete
                    v-model="filter.agencyDestinationCode"
                    :items="agenciesDestinationFilterItems"
                    :loading="agenciesDestinationFilter.isLoading"
                    :search-input.sync="agenciesDestinationFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('routes.agencyDestination')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesDestinationFilter.isEdit = true"
                    @change="agenciesDestinationFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col lg="3" sm="6" cols="12">
                  <v-dialog
                    ref="dialogFilterAutoschedulingCutOffTimeFrom"
                    v-model="modalFilterAutoschedulingCutOffTimeFrom"
                    :return-value.sync="
                      filter.autoschedulingCutOffTimeFromString
                    "
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.autoschedulingCutOffTimeFromString"
                        :label="$t('routes.autoschedulingCutOffTimeFrom')"
                        readonly
                        clearable
                        append-icon="far fa-clock"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="modalFilterAutoschedulingCutOffTimeFrom"
                      v-model="filter.autoschedulingCutOffTimeFromString"
                      full-width
                      format="24hr"
                      :allowed-minutes="allowedStepTime"
                      @click:minute="
                        $refs.dialogFilterAutoschedulingCutOffTimeFrom.save(
                          filter.autoschedulingCutOffTimeFromString
                        )
                      "
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterAutoschedulingCutOffTimeFrom = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
                <v-col lg="2" sm="6" cols="12">
                  <v-dialog
                    ref="dialogFilterAutoschedulingCutOffTimeTo"
                    v-model="modalFilterAutoschedulingCutOffTimeTo"
                    :return-value.sync="filter.autoschedulingCutOffTimeToString"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.autoschedulingCutOffTimeToString"
                        :label="$t('routes.autoschedulingCutOffTimeTo')"
                        readonly
                        clearable
                        append-icon="far fa-clock"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="modalFilterAutoschedulingCutOffTimeTo"
                      v-model="filter.autoschedulingCutOffTimeToString"
                      full-width
                      format="24hr"
                      :allowed-minutes="allowedStepTime"
                      @click:minute="
                        $refs.dialogFilterAutoschedulingCutOffTimeTo.save(
                          filter.autoschedulingCutOffTimeToString
                        )
                      "
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterAutoschedulingCutOffTimeTo = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel
              url="/routes"
              :filter="this.filter"
              :filterExport="filterExport"
            />
            <ImportExcel
              url="/routes"
              @done="search"
              v-if="$checkPermission('ROUTES')"
            />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr
                v-for="item in items"
                :key="item.agencyOriginCode + item.agencyDestinationCode"
              >
                <td>
                  <!-- Edit record -->
                  <crud-button
                    v-if="$checkPermission('ROUTES')"
                    x-small
                    color="orange"
                    icon="fa-pen"
                    :tooltip="$t('global.crud.buttons.edit')"
                    @clicked="
                      edit(item.agencyOriginCode, item.agencyDestinationCode)
                    "
                  ></crud-button>

                  <!-- Delete record -->
                  <crud-button
                    v-if="
                      (item.deletionDate === undefined ||
                        item.deletionDate === null) &&
                        $checkPermission('ROUTES')
                    "
                    x-small
                    color="red"
                    icon="fa-trash"
                    :tooltip="$t('global.crud.buttons.delete')"
                    @clicked="
                      suspend(item.agencyOriginCode, item.agencyDestinationCode)
                    "
                  ></crud-button>

                  <!-- Active record -->
                  <crud-button
                    v-else-if="$checkPermission('ROUTES')"
                    x-small
                    color="green"
                    icon="fa-redo"
                    :tooltip="$t('global.crud.buttons.restore')"
                    @clicked="
                      restore(item.agencyOriginCode, item.agencyDestinationCode)
                    "
                  ></crud-button>
                </td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.agencyOrigin.code }}</span>
                    </template>
                    <span>{{ item.agencyOrigin.name }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.agencyDestination.code }}</span>
                    </template>
                    <span>{{ item.agencyDestination.name }}</span>
                  </v-tooltip>
                </td>
                <td>{{ item.offsetDays }}</td>
                <td>
                  {{ minutesToHour(item.autoschedulingCutOffTime) }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-dialog v-model="dialog" max-width="600" persistent no-click-animation>
      <validation-observer ref="obs" v-slot="{ invalid, validated, passes }">
        <v-card>
          <v-toolbar dark color="primary" dense flat>
            <v-toolbar-title class="white--text">
              {{ formTitle }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="close()">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="details-list">
            <v-form>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    v-if="!editMode"
                    :name="$t('routes.agencyOrigin')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.agencyOriginCode"
                      :items="agenciesOriginItems"
                      :loading="agenciesOrigin.isLoading"
                      :search-input.sync="agenciesOrigin.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('routes.agencyOrigin')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      :error-messages="errors"
                      @focus="agenciesOrigin.isEdit = true"
                      @change="agenciesOrigin.isEdit = false"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                  <v-text-field
                    v-else
                    :label="$t('routes.agencyOrigin')"
                    v-model="item.agencyOriginString"
                    disabled
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    v-if="!editMode"
                    :name="$t('routes.agencyDestination')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.agencyDestinationCode"
                      :items="agenciesDestinationItems"
                      :loading="agenciesDestination.isLoading"
                      :search-input.sync="agenciesDestination.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('routes.agencyDestination')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      :error-messages="errors"
                      @focus="agenciesDestination.isEdit = true"
                      @change="agenciesDestination.isEdit = false"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                  <v-text-field
                    v-else
                    :label="$t('routes.agencyDestination')"
                    v-model="item.agencyDestinationString"
                    disabled
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('routes.offsetDays')"
                    rules="required|max:100"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('routes.offsetDays')"
                      v-model="item.offsetDays"
                      required
                      :error-messages="errors"
                      @keypress="integerOnlyField($event)"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <v-dialog
                    ref="dialogAutoschedulingCutOffTime"
                    v-model="modalAutoschedulingCutOffTime"
                    :return-value.sync="item.autoschedulingCutOffTimeString"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <validation-provider
                        :name="$t('routes.autoschedulingCutOffTime')"
                        v-slot="{ errors }"
                        vid="fieldAutoschedulingCutOffTime"
                      >
                        <v-text-field
                          v-model="item.autoschedulingCutOffTimeString"
                          :label="$t('routes.autoschedulingCutOffTime')"
                          readonly
                          clearable
                          append-icon="far fa-clock"
                          :error-messages="errors"
                          v-on="on"
                        ></v-text-field>
                      </validation-provider>
                    </template>
                    <v-time-picker
                      v-if="modalAutoschedulingCutOffTime"
                      v-model="item.autoschedulingCutOffTimeString"
                      full-width
                      format="24hr"
                      :allowed-minutes="allowedStepTime"
                      @click:minute="
                        $refs.dialogAutoschedulingCutOffTime.save(
                          item.autoschedulingCutOffTimeString
                        )
                      "
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalAutoschedulingCutOffTime = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="
                          $refs.dialogAutoschedulingCutOffTime.save(
                            item.autoschedulingCutOffTimeString
                          )
                        "
                      >
                        {{ $t("global.confirm") }}
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-flex>
              </v-layout>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click.native="close()">
              {{ $t("global.buttons.close") }}
            </v-btn>
            <v-btn v-if="editMode" color="orange" text @click="passes(update)">
              {{ $t("global.buttons.update") }}
            </v-btn>
            <v-btn v-else color="green" text @click="passes(save)">
              {{ $t("global.buttons.create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import ImportExcel from "@/components/ImportExcel.vue";
import { hourToMinutes, minutesToHour, integerOnlyField } from "@/js/utils.js";

const CONTEXT = "/routes";
const CONTEXT_AGENCIES = "/agencies";
const ENTITY_NAME = "route.";

export default {
  components: {
    CrudButton,
    ExportExcel,
    ImportExcel
  },
  name: "Routes",
  data: () => ({
    loading: false,
    searchTableWaiting: false,
    editMode: false,
    dialog: false,
    modalAutoschedulingCutOffTime: false,
    modalFilterAutoschedulingCutOffTimeFrom: false,
    modalFilterAutoschedulingCutOffTimeTo: false,
    item: {},
    items: [],
    filter: {
      allFields: "",
      agencyOriginCode: "",
      agencyDestinationCode: "",
      deletedItems: "ALL",
      autoschedulingCutOffTimeFrom: "",
      autoschedulingCutOffTimeFromString: "",
      autoschedulingCutOffTimeTo: "",
      autoschedulingCutOffTimeToString: ""
    },
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40],
    agenciesOrigin: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesDestination: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesOriginFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesDestinationFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    }
  }),
  computed: {
    deletedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: "ALL" },
        { text: this.$t("global.status.active"), value: "ACTIVE" },
        { text: this.$t("global.status.deleted"), value: "DELETED" }
      ];
    },
    formTitle() {
      return this.editMode
        ? this.$t("agencyParameters.title.edit")
        : this.$t("agencyParameters.title.new");
    },
    headers() {
      return [
        {
          text: this.$t("global.datatable.header.actions"),
          value: "actions",
          sortable: false
        },
        {
          text: this.$t("routes.datatable.agencyOrigin"),
          value: ENTITY_NAME + "agencyOriginCode"
        },
        {
          text: this.$t("routes.datatable.agencyDestination"),
          value: ENTITY_NAME + "agencyDestinationCode"
        },
        {
          text: this.$t("routes.datatable.offsetDays"),
          value: ENTITY_NAME + "offsetDays"
        },
        {
          text: this.$t("routes.datatable.autoschedulingCutOffTime"),
          value: ENTITY_NAME + "autoschedulingCutOffTime"
        }
      ];
    },
    agenciesOriginItems() {
      return this.agenciesOrigin.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesDestinationItems() {
      return this.agenciesDestination.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesOriginFilterItems() {
      return this.agenciesOriginFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesDestinationFilterItems() {
      return this.agenciesDestinationFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.allFields": function() {
      if (!this.searchTableWaiting) {
        setTimeout(() => {
          this.search();
          this.searchTableWaiting = false;
        }, 500);
      }
      this.searchTableWaiting = true;
    },
    "agenciesOrigin.search": function(value) {
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesOrigin,
        CONTEXT_AGENCIES
      );
    },
    "agenciesDestination.search": function(value) {
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesDestination,
        CONTEXT_AGENCIES
      );
    },
    "agenciesOriginFilter.search": function(value) {
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesOriginFilter,
        CONTEXT_AGENCIES
      );
    },
    "agenciesDestinationFilter.search": function(value) {
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesDestinationFilter,
        CONTEXT_AGENCIES
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      this.filter.autoschedulingCutOffTimeFrom =
        this.filter.autoschedulingCutOffTimeFromString != null
          ? hourToMinutes(this.filter.autoschedulingCutOffTimeFromString)
          : null;
      this.filter.autoschedulingCutOffTimeTo =
        this.filter.autoschedulingCutOffTimeToString != null
          ? hourToMinutes(this.filter.autoschedulingCutOffTimeToString)
          : null;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    preLoadGenericSearchEntity(value, object, context, aditionalFilter) {
      if (!object.isLoadingWaiting) {
        setTimeout(() => {
          this.genericSearchEntity(object, context, aditionalFilter);
          object.isLoadingWaiting = false;
        }, 500);
      }
      object.value = value;
      object.isLoadingWaiting = true;
    },
    async genericSearchEntity(object, context, aditionalFilter) {
      console.debug("genericSearchEntity");
      if (!object.isEdit || object.value === null || object.value === "")
        return;

      if (object.isLoading) return;

      object.isLoading = true;

      let filterGeneric = {
        allFields: object.value
      };
      if (aditionalFilter !== undefined) {
        Object.keys(aditionalFilter).forEach(
          key => (filterGeneric[key] = aditionalFilter[key])
        );
      }

      // Lazily load input items
      localhttp
        .post(context + "/allFilter", filterGeneric)
        .then(response => {
          console.debug(response.data);
          object.entries = response.data;
        })
        .catch(e => {
          console.log(e);
          this.$showError();
        })
        .finally(() => (object.isLoading = false));
    },
    create() {
      console.debug("Create element");
      this.editMode = false;
      this.resetDialog();
      this.dialog = true;
    },
    async save() {
      console.debug("Save form");

      this.item.autoschedulingCutOffTime = hourToMinutes(
        this.item.autoschedulingCutOffTimeString
      );

      localhttp
        .post(CONTEXT + "/create", this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    edit(agencyOrigin, agencyDestination) {
      console.debug("Edit element " + agencyOrigin + "/" + agencyDestination);
      this.editMode = true;
      this.resetDialog();
      localhttp
        .get(CONTEXT + "/" + agencyOrigin + "/" + agencyDestination)
        .then(response => {
          this.item = response.data;
          this.editPrepare(this.item);
          this.dialog = true;
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    editPrepare(item) {
      item.agencyOriginString =
        item.agencyOrigin.code + " - " + item.agencyOrigin.name;
      item.agencyDestinationString =
        item.agencyDestination.code + " - " + item.agencyDestination.name;
      item.autoschedulingCutOffTimeString = minutesToHour(
        item.autoschedulingCutOffTime
      );
    },
    async update() {
      console.debug("Update form");

      this.item.autoschedulingCutOffTime =
        this.item.autoschedulingCutOffTimeString != null
          ? hourToMinutes(this.item.autoschedulingCutOffTimeString)
          : null;

      localhttp
        .put(
          CONTEXT +
            "/update/" +
            this.item.agencyOriginCode +
            "/" +
            this.item.agencyDestinationCode,
          this.item
        )
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    async suspend(agencyOrigin, agencyDestination) {
      console.debug(
        "Suspend element " + agencyOrigin + "/" + agencyDestination
      );
      if (
        await this.$root.$confirm(
          this.$t("global.confirm"),
          this.$t("global.action.suspend.confirm"),
          { color: "blue" }
        )
      ) {
        localhttp
          .put(CONTEXT + "/delete/" + agencyOrigin + "/" + agencyDestination)
          .then(response => {
            console.log(response.data);
            this.search();
          })
          .catch(e => {
            console.log(e);
            this.$showErrorCode(e.response.status);
          });
      }
    },
    restore(agencyOrigin, agencyDestination) {
      console.debug(
        "Restore element " + agencyOrigin + "/" + agencyDestination
      );
      localhttp
        .put(CONTEXT + "/activate/" + agencyOrigin + "/" + agencyDestination)
        .then(response => {
          console.log(response.data);
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    refreshItemsView() {
      console.debug("Refresh table");
      this.search();
    },
    close() {
      this.dialog = false;
    },
    resetDialog() {
      this.item = {};
      this.resetAgenciesOriginSearch();
      this.resetAgenciesDestinationSearch();
      requestAnimationFrame(() => {
        if (this.$refs.obs !== undefined) {
          this.$refs.obs.reset();
        }
      });
    },
    resetAgenciesOriginSearch() {
      this.agenciesOrigin.search = "";
      this.agenciesOrigin.entries = [];
    },
    resetAgenciesDestinationSearch() {
      this.agenciesDestination.search = "";
      this.agenciesDestination.entries = [];
    },
    filterExport(jsonData) {
      jsonData.forEach(function(v) {
        delete v.agencyOrigin;
        delete v.agencyDestination;
      });
    },
    integerOnlyField,
    allowedStepTime: m => m % 5 === 0,
    minutesToHour,
    hourToMinutes
  }
};
</script>
